import React, { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";

/**
 * Layered floating bubbles + twinkling stars behind the hero + sections.
 * Uses framer-motion for depth variance and prefers-reduced-motion safe fallback.
 */
export default function FloatingBackground() {
  const reduced = useReducedMotion();

  const bubbles = useMemo(
    () =>
      Array.from({ length: 14 }).map((_, i) => ({
        id: i,
        left: `${(i * 73) % 100}%`,
        top: `${(i * 41) % 100}%`,
        size: 18 + ((i * 13) % 60),
        delay: (i % 7) * 0.4,
        dur: 6 + (i % 5),
        hue: i % 3,
      })),
    [],
  );

  const stars = useMemo(
    () =>
      Array.from({ length: 22 }).map((_, i) => ({
        id: i,
        left: `${(i * 47) % 100}%`,
        top: `${(i * 29) % 100}%`,
        size: 6 + ((i * 5) % 10),
        delay: (i % 5) * 0.6,
      })),
    [],
  );

  const palette = ["#BAE6FD", "#FDE68A", "#FECACA"];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {/* Aurora blobs */}
      <div
        className="aurora"
        style={{ width: 520, height: 520, background: "#BAE6FD", top: "-8%", left: "-8%" }}
      />
      <div
        className="aurora"
        style={{ width: 480, height: 480, background: "#FDE68A", top: "40%", right: "-10%" }}
      />
      <div
        className="aurora"
        style={{ width: 420, height: 420, background: "#FECACA", bottom: "-10%", left: "20%" }}
      />

      {/* Bubbles */}
      {bubbles.map((b) =>
        reduced ? (
          <span
            key={b.id}
            className="absolute rounded-full"
            style={{
              left: b.left,
              top: b.top,
              width: b.size,
              height: b.size,
              background: palette[b.hue],
              opacity: 0.35,
            }}
          />
        ) : (
          <motion.span
            key={b.id}
            className="absolute rounded-full"
            style={{
              left: b.left,
              top: b.top,
              width: b.size,
              height: b.size,
              background: palette[b.hue],
              opacity: 0.5,
              filter: "blur(0.5px)",
            }}
            animate={{ y: [0, -30, 0], x: [0, 12, 0] }}
            transition={{
              duration: b.dur,
              delay: b.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ),
      )}

      {/* Twinkling stars */}
      {stars.map((s) => (
        <span
          key={`s-${s.id}`}
          className="absolute tk-twinkle-anim"
          style={{
            left: s.left,
            top: s.top,
            width: s.size,
            height: s.size,
            animationDelay: `${s.delay}s`,
          }}
        >
          <svg viewBox="0 0 24 24" fill="#0b2a55" opacity="0.35">
            <path d="M12 2l2.3 6.9L21 10l-5.5 4.1L17 21l-5-3.7L7 21l1.5-6.9L3 10l6.7-1.1z" />
          </svg>
        </span>
      ))}
    </div>
  );
}
